import { LockOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import type { ErrorResponse } from "@shared/types";
import { useMutation } from "@tanstack/react-query";
import { App as AntdApp, Button, Card, Form, Input, Result, Typography } from "antd";
import type { AxiosError } from "axios";
import { useState } from "react";
import { authService } from "@/modules/auth/services/auth.service";
import type { LoginRequest } from "@/modules/auth/types";
import { PhoneInput } from "@/shared/components/PhoneInput";

const { Title, Text } = Typography;

export function DeleteAccountPage() {
	const [form] = Form.useForm<LoginRequest>();
	const { message } = AntdApp.useApp();
	const [isSuccess, setIsSuccess] = useState(false);

	const deleteAccountMutation = useMutation({
		mutationFn: (data: LoginRequest) => authService.login(data),
		onSuccess: () => {
			setIsSuccess(true);
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const errorMessage =
				error.response?.data?.error?.message || "Telefon raqam yoki parol noto'g'ri";
			message.error(errorMessage);
		},
	});

	const onFinish = (values: LoginRequest) => {
		deleteAccountMutation.mutate(values);
	};

	if (isSuccess) {
		return (
			<Card className="w-full max-w-md shadow-lg border-red-100">
				<Result
					status="success"
					title="So'rov qabul qilindi"
					subTitle="Ma'lumotlaringiz tekshirilib, 7 ish kunida hisobingiz o'chiriladi."
				/>
			</Card>
		);
	}

	return (
		<Card className="w-full max-w-md shadow-lg border-t-4 border-red-500">
			<div className="text-center mb-8">
				<div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
					<SafetyCertificateOutlined className="text-3xl text-red-500" />
				</div>
				<Title level={2} className="mb-2!">
					Hisobni o'chirish
				</Title>
			</div>

			<Form
				form={form}
				name="delete_account"
				onFinish={onFinish}
				layout="vertical"
				requiredMark={false}
				size="large"
			>
				<Form.Item
					name="phone"
					label="Telefon raqam"
					rules={[
						{
							required: true,
							message: "Telefon raqamingizni kiriting",
						},
						{
							pattern: /^\+998\d{9}$/,
							message: "To'liq 9 ta raqam kiriting",
						},
					]}
				>
					<PhoneInput id="delete-phone" size="large" />
				</Form.Item>

				<Form.Item
					name="password"
					label="Parol"
					rules={[
						{
							required: true,
							message: "Parolingizni kiriting",
						},
						{
							min: 4,
							message: "Parol kamida 4 ta belgidan iborat bo'lishi kerak",
						},
					]}
				>
					<Input.Password
						prefix={<LockOutlined className="text-gray-400" />}
						placeholder="Parol"
						autoComplete="current-password"
						id="delete-password"
					/>
				</Form.Item>

				<div className="bg-red-50 p-4 rounded-lg mb-6">
					<Text className="text-red-600 text-sm">
						<strong>Diqqat:</strong> Hisobingizni o'chirish tasdiqlangach, barcha ma'lumotlaringiz
						qaytarib bo'lmaydigan holatda yo'q qilinadi.
					</Text>
				</div>

				<Form.Item className="mb-0!">
					<Button
						type="primary"
						htmlType="submit"
						danger
						loading={deleteAccountMutation.isPending}
						block
						id="delete-submit"
					>
						O'chirish
					</Button>
				</Form.Item>
			</Form>
		</Card>
	);
}
